import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSortUp, faSortDown, faShip, faCalendarAlt, faBuilding, faTruck,
  faBoxes, faIdCard, faShieldAlt,
} from '@fortawesome/free-solid-svg-icons';
import { formatAmount } from '../../utils/amountFormat';
import { QUANTITY_DECIMALS, formatFigure } from '../../utils/precision';
import './VesselOverviewGrid.css';

/**
 * The "HMS Vessel Overview" column set, rendered from live deliveries.
 *
 * Trade Ops maintain this view as an Excel sheet (columns A-X). This table keeps
 * their column order and headers, but every value is the delivery record itself
 * -- there is no copy of the data here.
 *
 * STYLING: this is the Trade Ops deliveries table with more columns, so it uses
 * that table's own classes -- `.table-container`, `.data-table`, `.vessel-badge`,
 * `.transaction-number-badge`, `.created-at-badge`, `.clickable-row` (defined in
 * TradeOperationsLayout.css, already loaded by the parent screen). The scoped
 * `.vog-` rules below only add what 17 columns need on top of that: horizontal
 * scroll, truncation, and the sheet's grey-italic estimated-date signal.
 *
 * SCOPE: only the columns HMS actually stores are rendered. The sheet also has
 * B# (O), LC Cashed (Q), a second Industry (T), Pymt (U), Turnover (V),
 * Vsl Status (W) and Closed (X); none of those exist as fields anywhere in the
 * system, so they are omitted rather than shown as permanently empty columns.
 * Adding them is a data-model change (see the task card), not a display change.
 */

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Column order is the spreadsheet's, left to right, but the headers are spelled
// out in full rather than carrying the sheet's abbreviations -- nothing here is
// truncated and nothing needs a tooltip to be read. `xl` records which sheet
// column each one mirrors so a cross-check against the sheet stays possible.
// Widths are proportional hints; content wraps rather than being cut off.
export const XL_COLUMNS = [
  { key: 'year', xl: 'A', header: 'Year', width: 58, sort: 'number', muted: true },
  { key: 'month', xl: 'B', header: 'Month', width: 62, sort: 'month', muted: true },
  { key: 'vessel', xl: 'C', header: 'Vessel', width: 150 },
  { key: 'supplier', xl: 'D', header: 'Supplier', width: 130 },
  { key: 'buyer', xl: 'E', header: 'Buyer / End User', width: 145 },
  // For a bunkers delivery `product_origin` is overwritten with the BUNKERING
  // PORT's country, so the same field means something different and is labelled
  // accordingly -- the fuelsLabel mechanism widgetDefinitions.js already uses.
  { key: 'origin', xl: 'F', header: 'Origin', fuelsLabel: 'Delivery Location', width: 78 },
  // Nothing writes delivery_destination on the fuels path at all.
  { key: 'dest', xl: 'G', header: 'Destination', naForFuels: true, width: 100 },
  { key: 'product', xl: 'H', header: 'Product', width: 135 },
  { key: 'qty', xl: 'I', header: 'Quantity', width: 90, sort: 'number', numeric: true },
  { key: 'blDate', xl: 'J', header: 'Bill of Lading Date', fuelsLabel: 'BDN Date', width: 105, sort: 'date', numeric: true },
  { key: 'laycan', xl: 'K', header: 'Laycan', fuelsLabel: 'Expected Delivery Window', width: 110 },
  { key: 'ops', xl: 'L', header: 'Operator', width: 105 },
  { key: 'trader', xl: 'M', header: 'Trader', width: 115 },
  // Incoterms are hidden on the fuels side of the delivery form, so they are
  // never set for bunkers.
  { key: 'inco', xl: 'N', header: 'Incoterms', naForFuels: true, width: 100 },
  // The sheet's column O is `B#`. HMS's own equivalent identity for a shipment is
  // the delivery number, so it takes that slot rather than leaving a gap.
  { key: 'deliveryNumber', xl: 'O', header: 'Delivery Number', width: 118 },
  { key: 'entity', xl: 'P', header: 'IMT Entity', width: 100 },
  { key: 'notes', xl: 'R', header: 'Notes', width: 170 },
  { key: 'industry', xl: 'S', header: 'Industry', width: 150 },
];

// One shipment occupies TWO stacked rows of 9 columns rather than one row of 18.
// Eighteen columns on a line forced sideways scrolling; split in half at the
// sheet's own seam (A-I above, J-S below) the whole record fits any screen with
// nothing to scroll and nothing cut off.
export const TOP_SLOTS = XL_COLUMNS.slice(0, 9);
export const BOTTOM_SLOTS = XL_COLUMNS.slice(9);

// Both rows share one set of column widths, so each slot is sized for the wider
// of the two fields that sit in it (slot 1 carries Year above Bill of Lading
// Date, slot 3 Vessel above Operator, and so on).
export const SLOT_WIDTHS = [105, 100, 150, 150, 150, 112, 105, 150, 130];
export const SLOT_WIDTH_TOTAL = SLOT_WIDTHS.reduce((sum, w) => sum + w, 0);

// Sheet columns deliberately not rendered, kept here so the omission is
// documented in code rather than implied by absence.
export const OMITTED_XL_COLUMNS = [
  { xl: 'Q', header: 'LC Cashed' },
  { xl: 'T', header: 'Industry (2nd)' },
  { xl: 'U', header: 'Pymt' },
  { xl: 'V', header: 'Turnover' },
  { xl: 'W', header: 'Vsl Status' },
  { xl: 'X', header: 'Closed' },
];

const parseIsoDate = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
};

const formatDmy = (parts) => (
  parts ? `${String(parts.d).padStart(2, '0')}/${String(parts.m).padStart(2, '0')}/${parts.y}` : ''
);

// Thousands separators are pinned to en-US so the grid matches the sheet
// regardless of the browser's locale (a de-DE browser would otherwise render
// 75.000 and read as seventy-five). Decimals show only when the quantity
// carries them, up to QUANTITY_DECIMALS -- a tonnage keeps every decimal the
// chain stores instead of being cut to two. The string is read as written
// (no float), so a long six-decimal tonnage is not nudged in its last digit.
const formatQty = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  const figure = formatFigure(raw, 0, QUANTITY_DECIMALS) || formatFigure(n, 0, QUANTITY_DECIMALS);
  return formatAmount(figure);
};

const firstNonEmpty = (...values) => {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s !== '') return s;
  }
  return '';
};

// A delivery can carry several recaps per leg; the active one is what the rest
// of the delivery screens show, so the grid follows it. Falls back to the first
// linked recap when nothing is marked active (older records).
const pickActiveRecap = (rows, activeId) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (activeId !== null && activeId !== undefined) {
    const hit = rows.find(r => String(r.id) === String(activeId));
    if (hit) return hit;
  }
  return rows[0];
};

// Every distinct product on one leg of the deal.
//
// A shipment can carry more than one product by four routes: several recaps
// linked to one leg (both commodities), several TradeRecapProduct child rows on
// one fuels recap, per-product allocation rows on the fuels sales leg, and the
// two legs simply disagreeing. Showing only the first one -- with the first
// one's tonnage beside it -- states a single product as if it were the whole
// shipment. So the labels are collected and the count is shown.
const productLabelsFor = (recaps, activeId) => {
  const rows = Array.isArray(recaps) ? recaps : [];
  const ordered = [];
  const active = pickActiveRecap(rows, activeId);
  // Active recap first, then the rest, so the headline label is the one the
  // delivery screens treat as current.
  [active, ...rows.filter(r => r !== active)].filter(Boolean).forEach((recap) => {
    const kids = Array.isArray(recap.recap_products) ? recap.recap_products : [];
    const fromKids = kids
      .map(p => String(p.product_label || '').trim())
      .filter(Boolean);
    const labels = fromKids.length
      ? fromKids
      : [firstNonEmpty(recap.product, recap.fuel_contract_shorthand)].filter(Boolean);
    labels.forEach(l => { if (!ordered.includes(l)) ordered.push(l); });
  });
  return ordered;
};

// Sum of the per-product BDN tonnages on a fuels sales recap.
//
// Deliberately reads `final_tonnage_mt` and NEVER `allocated_quantity`: the
// latter is double-counted at source (a 200 MT contract reports 400), so summing
// it would put a confidently wrong number on screen.
const bdnTonnageFor = (recap) => {
  const allocs = Array.isArray(recap?.allocations) ? recap.allocations : [];
  const values = allocs
    .map(a => Number(a.final_tonnage_mt))
    .filter(n => Number.isFinite(n));
  if (values.length === 0) return null;
  return values.reduce((sum, n) => sum + n, 0);
};

// Earliest BDN date across a fuels recap's per-product allocation rows. For
// bunkers this is the date of record; `bill_of_lading_date` is often never set,
// which is why Year / Month / BDN Date used to render blank on a shipment that
// had actually been delivered.
const earliestBdnDate = (recaps) => {
  const dates = (Array.isArray(recaps) ? recaps : [])
    .flatMap(r => (Array.isArray(r.allocations) ? r.allocations : []))
    .map(a => a.bdn_date)
    .filter(Boolean)
    .sort();
  return dates.length ? dates[0] : null;
};

/**
 * Resolve one compliance status into something safe to put on screen.
 *
 * `buyer_kyc_status` and friends default to 'pending' on the model, so EVERY
 * delivery carries 'pending' whether or not anyone ever screened the party. A
 * bare default rendered as an amber "Pending" chip would read as "screening in
 * progress" when the truth is "we never looked" -- so it is dropped.
 *
 * A decided value (approved / flagged / cleared / sanctioned) always shows, even
 * with no CRM profile linked, because someone set it deliberately and a real flag
 * must never be hidden.
 */
export const resolveComplianceStatus = (raw, hasCrmProfile) => {
  const s = String(raw || '').trim().toLowerCase();
  if (s === '') return null;
  if (s === 'pending') return hasCrmProfile ? 'pending' : null;
  return s;
};

/**
 * One delivery -> one spreadsheet row.
 *
 * Blank stays blank: a missing B/L date does not become today, and a missing
 * quantity does not become 0. The only substitutions are the sheet's own
 * placeholders -- TBN for a vessel not yet nominated, TBA for an unnamed
 * counterparty, and `--` for a missing side of the incoterms pair.
 */
export const buildOverviewRow = (d) => {
  const purchase = pickActiveRecap(d.purchase_recaps_data, d.active_purchase_recap_id);
  const sales = pickActiveRecap(d.sales_recaps_data, d.active_sales_recap_id);

  // Bunkers and cargo do not share a source of truth for quantity or for the
  // date of record, so the row resolves per commodity instead of assuming cargo.
  const isFuels = d.commodity_type === 'fuels';

  // Cargo's date of record is the B/L; bunkers' is the per-product BDN date, and
  // bill_of_lading_date is frequently never written on the fuels path.
  const blIso = firstNonEmpty(
    d.bill_of_lading_date,
    isFuels ? earliestBdnDate(d.sales_recaps_data) : '',
  );
  const bl = parseIsoDate(blIso);
  const blIsFinal = String(d.bill_of_lading_date_estimated_or_final || '').toLowerCase() === 'final';

  // Cargo: the B/L quantity. Bunkers: the delivered tonnage -- the delivery's own
  // final_tonnage_mt, else the sum of the per-product BDN tonnages.
  const blQty = firstNonEmpty(d.bill_of_lading_quantity);
  let qtyRaw;
  if (isFuels) {
    const bdnSum = bdnTonnageFor(pickActiveRecap(d.sales_recaps_data, d.active_sales_recap_id));
    qtyRaw = firstNonEmpty(d.final_tonnage_mt) !== ''
      ? d.final_tonnage_mt
      : (bdnSum !== null ? bdnSum : (blQty !== '' ? d.bill_of_lading_quantity : d.delivery_product_quantity));
  } else {
    qtyRaw = blQty !== '' ? d.bill_of_lading_quantity : d.delivery_product_quantity;
  }
  const qtyNum = Number(qtyRaw);

  // Intercompany deliveries show both sides the way the sheet does.
  // NOTE: `intercompany_entities` holds HMS entity IDS (copied from
  // TradeRecap.entity), so the resolved *_names block is the one to read; the raw
  // block is only a last-resort fallback for a payload predating that field.
  const intercoNames = d.intercompany_entity_names || {};
  const intercoRaw = d.intercompany_entities || {};
  const intercoSide = side => (
    Array.isArray(intercoNames[side]) && intercoNames[side].length
      ? intercoNames[side]
      : (Array.isArray(intercoRaw[side]) ? intercoRaw[side] : [])
  );
  const interco = d.is_intercompany
    ? Array.from(new Set(
      [...intercoSide('purchase'), ...intercoSide('sales')]
        .map(v => String(v).trim()).filter(Boolean)
    ))
    : [];

  const traders = Array.from(new Set(
    [d.delivery_deal_maker_purchase, d.delivery_deal_maker_sale]
      .map(v => (v === null || v === undefined ? '' : String(v).trim()))
      .filter(Boolean)
  ));

  // Sales leg first (what we are selling is what the shipment is), falling back
  // to the purchase leg when nothing is sold yet.
  const salesLabels = productLabelsFor(d.sales_recaps_data, d.active_sales_recap_id);
  const productLabels = salesLabels.length
    ? salesLabels
    : productLabelsFor(d.purchase_recaps_data, d.active_purchase_recap_id);

  const industries = d.buyer_crm_data?.industry_selections;
  const hasBuyerProfile = Boolean(d.buyer_crm_data);
  const hasSellerProfile = Boolean(d.seller_crm_data);

  return {
    id: d.id,
    deliveryNumber: d.delivery_unique_number || '',
    // Per-row, not per-screen: a mixed-commodity list stays correct.
    isFuels,
    year: bl ? String(bl.y) : '',
    month: bl ? MONTH_LABELS[bl.m - 1] : '',
    monthValue: bl ? bl.m : null,
    vessel: firstNonEmpty(d.vessel_name) || 'TBN',
    supplier: firstNonEmpty(d.seller_crm_profile_name, d.delivery_producer, purchase?.purchase_seller) || 'TBA',
    buyer: firstNonEmpty(d.buyer_crm_profile_name, d.delivery_end_user, sales?.sale_buyer) || 'TBA',
    origin: firstNonEmpty(d.product_origin),
    dest: firstNonEmpty(d.delivery_destination),
    // Every distinct product on the active leg. The cell shows the first plus a
    // "+N more" marker, with the full list on hover -- so a multi-product
    // shipment can never read as a single-product one.
    products: productLabels,
    product: productLabels[0] || '',
    productExtra: Math.max(0, productLabels.length - 1),
    qty: formatQty(qtyRaw),
    qtyValue: Number.isFinite(qtyNum) && qtyRaw !== '' && qtyRaw !== null ? qtyNum : null,
    blDate: formatDmy(bl),
    blDateSort: d.bill_of_lading_date || '',
    // The sheet greys + italicises an estimated B/L date and prints a final one
    // in black. Same signal here, from the same flag the delivery already sets.
    blEstimated: Boolean(bl) && !blIsFinal,
    laycan: firstNonEmpty(d.delivery_laycan),
    ops: firstNonEmpty(d.delivery_main_operator),
    trader: traders.join(' / '),
    inco: `${firstNonEmpty(d.incoterms_purchase) || '--'}/${firstNonEmpty(d.incoterms_sale) || '--'}`,
    entity: interco.length ? interco.join('/') : firstNonEmpty(d.hms_entity_name, d.hms_entity),
    // `additional_notes` has no writer anywhere in the app -- the delivery form's
    // only notes editor writes `internal_notes` -- so this column was structurally
    // blank. Reads the field that is actually filled, keeping the legacy one as a
    // fallback for older rows.
    notes: firstNonEmpty(d.internal_notes, d.additional_notes).replace(/\s+/g, ' '),
    industry: Array.isArray(industries) ? industries.filter(Boolean).join(', ') : '',

    // Counterparty screening, shown next to the name it belongs to.
    compliance: {
      buyerKyc: resolveComplianceStatus(d.buyer_kyc_status, hasBuyerProfile),
      buyerSanction: resolveComplianceStatus(d.buyer_sanction_status, hasBuyerProfile),
      sellerKyc: resolveComplianceStatus(d.seller_kyc_status, hasSellerProfile),
      sellerSanction: resolveComplianceStatus(d.seller_sanction_status, hasSellerProfile),
    },

    // Backing detail for the HMS Entity hover panel. Everything here is already
    // in the delivery payload -- the panel reveals it, it doesn't fetch it.
    entityDetail: {
      isIntercompany: Boolean(d.is_intercompany),
      purchase: intercoSide('purchase'),
      sales: intercoSide('sales'),
      primary: firstNonEmpty(d.hms_entity_name, d.hms_entity),
      bank: firstNonEmpty(d.hms_crm_data?.primary_bank?.bank_name),
      office: firstNonEmpty(d.delivery_assigned_hms_office),
    },
  };
};

const sortValue = (row, col) => {
  switch (col.sort) {
    case 'number':
      return col.key === 'qty' ? row.qtyValue : (row[col.key] === '' ? null : Number(row[col.key]));
    case 'month':
      return row.monthValue;
    case 'date':
      return row.blDateSort || '';
    default:
      return (row[col.key] || '').toLowerCase();
  }
};

// Blanks always sink to the bottom, in both directions -- an empty cell is
// missing information, not a smallest value.
const compareRows = (a, b, col, dir) => {
  const av = sortValue(a, col);
  const bv = sortValue(b, col);
  const aEmpty = av === null || av === undefined || av === '';
  const bEmpty = bv === null || bv === undefined || bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (av === bv) return 0;
  return (av < bv ? -1 : 1) * (dir === 'asc' ? 1 : -1);
};

// Which colour family each column's chip borrows, and why. The families are the
// ones already in use across Trade Ops, so the colour carries the same meaning
// here as it does everywhere else:
//   blue   = purchase leg   (.recap-name-badge.purchase)
//   green  = sale leg       (.recap-name-badge.sale)
//   indigo = vessel         (.vessel-badge)
//   purple = product/stock  (.trade-recap-summary-badge)
//   amber  = contract terms (.status-badge.status-partial)
//   slate  = geography      (new, paired so Origin and Destination read as a set)
//   grey   = dates          (.created-at-badge)
// Columns not listed stay plain text -- 17 pills in a row would carry no signal.
// Supplier and Buyer are handled separately (they carry screening chips too);
// their families are declared here so the scheme stays readable in one place.
const CHIP_FAMILY = {
  product: 'product',
  origin: 'place',
  dest: 'place',
  inco: 'terms',
  industry: 'industry',
};

// Colour follows the CRM screens exactly -- green cleared/approved, amber
// pending, red flagged/sanctioned -- so a chip means the same thing here as it
// does on the partner profile. Icon-only because a 17-column row has no space
// for the word; the status is spelled out in the tooltip.
const COMPLIANCE_TONE = {
  cleared: 'ok',
  approved: 'ok',
  pending: 'warn',
  flagged: 'bad',
  sanctioned: 'bad',
};

const ComplianceChip = ({ kind, side, status }) => {
  if (!status) return null;
  const tone = COMPLIANCE_TONE[status] || 'warn';
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const what = kind === 'kyc' ? 'KYC' : 'Sanctions';
  return (
    <span
      className={`vog-compliance vog-compliance-${tone} ${status === 'sanctioned' ? 'vog-compliance-alarm' : ''}`}
      title={`${side} ${what}: ${label}`}
      aria-label={`${side} ${what}: ${label}`}
    >
      <FontAwesomeIcon icon={kind === 'kyc' ? faIdCard : faShieldAlt} />
    </span>
  );
};

const VesselOverviewGrid = ({
  deliveries = [],
  onOpenDelivery,
  hoveredVesselKey = null,
  onHoverVessel,
}) => {
  const [sortKey, setSortKey] = useState('blDate');
  const [sortDir, setSortDir] = useState('desc');

  const rows = useMemo(() => {
    const built = deliveries.map(buildOverviewRow);
    const col = XL_COLUMNS.find(c => c.key === sortKey);
    if (col) built.sort((a, b) => compareRows(a, b, col, sortDir));
    return built;
  }, [deliveries, sortKey, sortDir]);

  const toggleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(key === 'blDate' ? 'desc' : 'asc');
      return key;
    });
  }, []);

  // Identity-ish columns get the same badges the deliveries table uses; the rest
  // stay plain text so 18 columns don't turn into a wall of pills.
  const renderCell = (row, col) => {
    const value = row[col.key];

    // Some columns are cargo-only: bunkers never writes a destination, and the
    // incoterm fields are hidden on the fuels form. Saying so beats an em-dash,
    // which would read as "someone forgot to fill this in".
    if (col.naForFuels && row.isFuels) {
      return (
        <span className="vog-chip vog-chip-na" title={`${col.header} is not used for bunkers deliveries`}>
          n/a
        </span>
      );
    }

    // Product: the headline label plus how many more this shipment carries.
    if (col.key === 'product' && row.products.length > 0) {
      const extra = row.productExtra;
      const chip = (
        <span className="vog-chip vog-chip-product">{row.product}</span>
      );
      if (extra === 0) return chip;
      return (
        <div className="trade-recap-hover-container vog-products">
          {chip}
          <span className="vog-chip vog-chip-more">{`+${extra} more`}</span>
          <div className="trade-recap-details-dropdown vog-entity-panel">
            <div className="vog-entity-panel-title">
              {`${row.products.length} products on this shipment`}
            </div>
            {row.products.map((label, i) => (
              <div className="vog-entity-row" key={label}>
                <span className="vog-entity-key">{i + 1}</span>
                <span className="vog-entity-val">{label}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // A cell with nothing in it says so, rather than leaving a labelled void the
    // reader has to interpret. Deliberately distinct from the sheet's TBN / TBA /
    // `--`, which mean "not nominated yet" -- that is information, this is its
    // absence.
    if (value === null || value === undefined || String(value).trim() === '') {
      return (
        <span className="vog-chip vog-chip-empty" title={`No ${col.header.toLowerCase()} recorded`}>
          —
        </span>
      );
    }

    if (col.key === 'vessel') {
      const unnamed = value === 'TBN';
      return (
        <div className={`vessel-badge ${unnamed ? 'vog-badge-none' : ''}`}>
          <FontAwesomeIcon icon={faShip} className="vessel-icon" />
          <span className="vessel-text">{value}</span>
        </div>
      );
    }

    if (col.key === 'deliveryNumber') {
      // Same badge the deliveries table uses for a delivery number.
      return (
        <div className="transaction-number-badge">
          <FontAwesomeIcon icon={faBoxes} className="transaction-icon" />
          <span className="transaction-text">{value}</span>
        </div>
      );
    }

    // Screening sits next to the party it belongs to, not in its own column --
    // the point is to see the flag while reading the name.
    if (col.key === 'supplier' || col.key === 'buyer') {
      const isBuyer = col.key === 'buyer';
      const c = row.compliance;
      const unknown = value === 'TBA';
      return (
        <div className="vog-party">
          <span className={`vog-chip vog-chip-${isBuyer ? 'sale' : 'purchase'} ${unknown ? 'vog-chip-none' : ''}`}>
            {value}
          </span>
          <span className="vog-party-flags">
            <ComplianceChip
              kind="sanction"
              side={isBuyer ? 'Buyer' : 'Supplier'}
              status={isBuyer ? c.buyerSanction : c.sellerSanction}
            />
            <ComplianceChip
              kind="kyc"
              side={isBuyer ? 'Buyer' : 'Supplier'}
              status={isBuyer ? c.buyerKyc : c.sellerKyc}
            />
          </span>
        </div>
      );
    }

    if (col.key === 'entity') {
      const d = row.entityDetail;
      // Hover reveals the entity behind the code: both legs when the deal is
      // intercompany, plus the office and bank already on the payload. Same
      // hover-container idiom the deliveries table uses for its recap list.
      return (
        <div className="trade-recap-hover-container vog-entity">
          {/* HMS navy, deliberately not the delivery-number blue now sitting in
              the column beside it. */}
          <span className="vog-chip vog-chip-entity vog-entity-badge">
            <FontAwesomeIcon icon={faBuilding} className="vog-chip-icon" />
            {value}
          </span>
          <div className="trade-recap-details-dropdown vog-entity-panel">
            <div className="vog-entity-panel-title">
              {d.isIntercompany ? 'Intercompany deal' : 'IMT entity'}
            </div>
            {d.isIntercompany ? (
              <>
                <div className="vog-entity-row">
                  <span className="vog-entity-key">Purchase</span>
                  <span className="vog-entity-val">{d.purchase.join(', ') || '—'}</span>
                </div>
                <div className="vog-entity-row">
                  <span className="vog-entity-key">Sales</span>
                  <span className="vog-entity-val">{d.sales.join(', ') || '—'}</span>
                </div>
              </>
            ) : (
              <div className="vog-entity-row">
                <span className="vog-entity-key">Entity</span>
                <span className="vog-entity-val">{d.primary || '—'}</span>
              </div>
            )}
            {d.office && (
              <div className="vog-entity-row">
                <span className="vog-entity-key">Office</span>
                <span className="vog-entity-val">{d.office}</span>
              </div>
            )}
            {d.bank && (
              <div className="vog-entity-row">
                <span className="vog-entity-key">Bank</span>
                <span className="vog-entity-val">{d.bank}</span>
              </div>
            )}
            {/* Delivery number deliberately not repeated here -- it has its own
                column now (sheet slot O). */}
          </div>
        </div>
      );
    }

    if (col.key === 'blDate') {
      // An estimated B/L date reads grey + italic, a final one solid -- the same
      // signal the spreadsheet uses, from the flag the delivery already sets.
      return (
        <div className={`created-at-badge ${row.blEstimated ? 'vog-estimated' : ''}`}>
          <FontAwesomeIcon icon={faCalendarAlt} className="created-at-icon" />
          <span className="created-at-text">{value}</span>
        </div>
      );
    }

    const family = CHIP_FAMILY[col.key];
    if (family) {
      const unknown = value === 'TBA' || value === '--/--';
      return (
        <span className={`vog-chip vog-chip-${family} ${unknown ? 'vog-chip-none' : ''}`}>
          {value}
        </span>
      );
    }

    const classes = ['vog-text'];
    if (col.numeric) classes.push('vog-num');
    if (col.muted) classes.push('vog-muted');
    return <span className={classes.join(' ')}>{value}</span>;
  };

  // Every cell carries its own label directly above its value. With two lines per
  // shipment, a header row sits too far from the second line to be read against
  // it -- so there is no header row at all; the label travels with the value.
  // The label doubles as the sort control for its column, and stops the click
  // from reaching the row (which would open the delivery instead).
  const renderBodyCells = (row, slots) => slots.map((col) => {
    const isSorted = sortKey === col.key;
    // Same expression DeliveryWidgetCard uses: one optional key on the column,
    // resolved per row, so a bunkers shipment reads "BDN Date" and a cargo one
    // reads "Bill of Lading Date" in the very same list.
    const header = (col.fuelsLabel && row.isFuels) ? col.fuelsLabel : col.header;
    return (
      <td key={col.key} className="vog-td">
        <button
          type="button"
          className={`vog-label ${isSorted ? 'vog-label-sorted' : ''}`}
          title={`Sheet column ${col.xl} — click to sort by ${header}`}
          onClick={(e) => { e.stopPropagation(); toggleSort(col.key); }}
        >
          {header}
          {isSorted && (
            <FontAwesomeIcon
              className="vog-sort-icon"
              icon={sortDir === 'asc' ? faSortUp : faSortDown}
            />
          )}
        </button>
        <div className="vog-value">{renderCell(row, col)}</div>
      </td>
    );
  });

  return (
    <section className="deliveries-section vog-section">
      {/* 9 proportional columns per line, so there is nothing to scroll sideways.
          NOTE: `.table-container` is `overflow: visible`, but the card wrapper two
          levels up (`.so-container`) used to be `overflow: hidden` -- that, not
          this table, was clipping the hover panels and amputating the right-hand
          columns. Fixed in ShipmentOverview.css; if a panel ever gets cut off
          again, look at the ancestors first. */}
      <div className="table-container">
        <table className="data-table vog-table">
            <colgroup>
              {/* Percentages, not px: under `table-layout: fixed` absolute widths
                  floor the table at their total, and anything wider than the
                  container was being clipped by the card wrapper with no
                  scrollbar to reach it. */}
              {SLOT_WIDTHS.map((w, i) => (
                <col key={i} style={{ width: `${((w / SLOT_WIDTH_TOTAL) * 100).toFixed(4)}%` }} />
              ))}
            </colgroup>
            {/* No <thead>: every cell labels itself (see renderBodyCells). */}
            {rows.length > 0 ? (
              rows.map((row, idx) => {
                // Same vessel key the cards and the map markers use, so hovering
                // either line lights that vessel on the map -- and hovering the
                // marker lights both lines back.
                const vesselKey = `d:${row.id}`;
                const isHot = hoveredVesselKey === vesselKey;
                // One <tbody> per shipment: the two <tr>s are a single record, so
                // they hover, highlight, click and stripe as one unit.
                const pairClass = [
                  'vog-pair',
                  idx % 2 === 1 ? 'vog-pair-alt' : '',
                  isHot ? 'vog-pair-hot' : '',
                ].filter(Boolean).join(' ');
                const rowProps = {
                  className: 'clickable-row',
                  onClick: () => onOpenDelivery && onOpenDelivery(row.id),
                  onMouseEnter: () => onHoverVessel && onHoverVessel(vesselKey),
                  onMouseLeave: () => onHoverVessel && onHoverVessel(null),
                  title: row.deliveryNumber ? `Open ${row.deliveryNumber}` : 'Open delivery',
                };
                return (
                  <tbody key={row.id} className={pairClass}>
                    {/* No per-cell title: nothing is cut off, so there is
                        nothing a tooltip would need to reveal. */}
                    <tr {...rowProps} className={`${rowProps.className} vog-row-top`}>
                      {renderBodyCells(row, TOP_SLOTS)}
                    </tr>
                    <tr {...rowProps} className={`${rowProps.className} vog-row-bottom`}>
                      {renderBodyCells(row, BOTTOM_SLOTS)}
                    </tr>
                  </tbody>
                );
              })
            ) : (
              <tbody>
                <tr>
                  <td colSpan={SLOT_WIDTHS.length} className="empty-state-cell">
                    <div className="empty-state-container">
                      <div className="empty-state-icon">
                        <FontAwesomeIcon icon={faTruck} />
                      </div>
                      <div className="empty-state-text">
                        <h3>No Shipments Yet</h3>
                        <p>Deliveries for this year will appear here</p>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
        </table>
      </div>
    </section>
  );
};

export default VesselOverviewGrid;
